import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, ShieldAlert, Award, FileText, CheckCircle, AlertCircle, Loader2, Sparkles, Code, Eye } from 'lucide-react';
import { auth } from '../lib/firebase';
import { authedFetch } from '../lib/db';

interface ProfileProps {
  userEmail: string;
  onProfileUpdate?: (user: any) => void;
}

export default function Profile({ userEmail, onProfileUpdate }: ProfileProps) {
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [signature, setSignature] = useState('');
  const [editMode, setEditMode] = useState<'wysiwyg' | 'html'>('wysiwyg');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authedFetch('/api/profile');
      if (!res.ok) throw new Error('Failed to load user profile');
      const data = await res.json();
      setDisplayName(data.displayName || '');
      setRole(data.role || 'sales');
      setCreatedAt(data.createdAt || '');
      setSignature(data.signature || '');
      
      if (editorRef.current) {
        editorRef.current.innerHTML = data.signature || '';
      }
    } catch (err: any) {
      setError(err.message || 'Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Sync editorRef with signature state on load or editMode toggle
  useEffect(() => {
    if (!loading && editMode === 'wysiwyg' && editorRef.current) {
      if (editorRef.current.innerHTML !== signature) {
        editorRef.current.innerHTML = signature;
      }
    }
  }, [loading, editMode, signature]);

  // Update contentEditable when signature state is changed by templates,
  // but only if user is in WYSIWYG mode and not typing.
  const updateEditorContent = (htmlContent: string) => {
    setSignature(htmlContent);
    if (editorRef.current) {
      editorRef.current.innerHTML = htmlContent;
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      setSignature(editorRef.current.innerHTML);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const finalSignature = editMode === 'wysiwyg' && editorRef.current
      ? editorRef.current.innerHTML
      : signature;

    try {
      const res = await authedFetch('/api/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ displayName, signature: finalSignature })
      });
      if (!res.ok) throw new Error('Failed to save profile changes');

      if (onProfileUpdate) {
        onProfileUpdate({
          uid: auth.currentUser?.uid || '',
          email: userEmail,
          displayName,
          role: role as any,
          createdAt,
          signature: finalSignature
        });
      }

      setSuccess('Profile and signature updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  // Generate dynamic Armellini Logistics signature template based on profile values
  const insertArmelliniTemplate = () => {
    const name = displayName || 'Mark Sterling';
    const email = userEmail || 'msterling@armellini.com';
    const phoneDirect = '305.591.5575';
    const phoneOffice = '772.287.0575';
    const phoneCell = '786.471.5235';
    
    // HTML email signature optimized for Outlook, Gmail, and standard mail clients
    const template = `<table cellpadding="0" cellspacing="0" style="font-family: 'Space Grotesk', Inter, Helvetica, Arial, sans-serif; font-size: 13px; line-height: 18px; color: #1e293b; max-width: 600px;">
  <tr>
    <!-- Logo block on the left -->
    <td valign="middle" style="padding-right: 20px;">
      <a href="https://www.armellini.com" target="_blank" style="text-decoration: none;">
        <img src="${window.location.origin}/logo.png" alt="Armellini Logistics" width="200" style="display: block; border: 0; outline: none; object-fit: contain;" />
      </a>
    </td>
    
    <!-- Vertical divider -->
    <td valign="top" style="border-left: 2px solid #cbd5e1; padding-left: 20px;">
      <div style="font-size: 16px; font-weight: 700; color: #000000; margin-bottom: 2px;">${name}</div>
      <div style="font-size: 11px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px;">${role.toUpperCase()}</div>
      
      <div style="margin-bottom: 8px;">
        <a href="mailto:${email}" style="color: #0d6efd; text-decoration: underline; font-weight: 600;">${email}</a>
      </div>
      
      <div style="font-size: 12px; color: #334155; margin-bottom: 10px; font-family: monospace;">
        <div><strong>Direct:</strong> ${phoneDirect}</div>
        <div><strong>Office:</strong> ${phoneOffice}</div>
        <div><strong>Cell:</strong> ${phoneCell}</div>
      </div>
      
      <div style="font-size: 12px; color: #475569; line-height: 16px; margin-bottom: 10px;">
        <strong>Armellini Express Lines Inc.</strong><br />
        <a href="https://www.armellini.com" target="_blank" style="color: #0d6efd; text-decoration: underline;">www.armellini.com</a><br />
        3446 SW Armellini Ave,<br />
        Palm City, FL 34990
      </div>
      
      <div style="margin-bottom: 12px;">
        <a href="https://www.armellini.com/schedule" target="_blank" style="color: #0d6efd; text-decoration: underline; font-weight: 700;">Book a Meeting With Me</a>
      </div>
      
      <!-- Social icons -->
      <table cellpadding="0" cellspacing="0">
        <tr>
          <td style="padding-right: 8px;">
            <a href="https://twitter.com/armellinilog" target="_blank" style="display: inline-block; width: 22px; height: 22px; line-height: 22px; text-align: center; border-radius: 50%; background-color: #334155; color: #ffffff; text-decoration: none; font-size: 11px; font-weight: bold;">t</a>
          </td>
          <td style="padding-right: 8px;">
            <a href="https://facebook.com/armellini" target="_blank" style="display: inline-block; width: 22px; height: 22px; line-height: 22px; text-align: center; border-radius: 50%; background-color: #334155; color: #ffffff; text-decoration: none; font-size: 11px; font-weight: bold;">f</a>
          </td>
          <td style="padding-right: 8px;">
            <a href="https://linkedin.com/company/armellini" target="_blank" style="display: inline-block; width: 22px; height: 22px; line-height: 22px; text-align: center; border-radius: 50%; background-color: #334155; color: #ffffff; text-decoration: none; font-size: 11px; font-weight: bold;">in</a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

    updateEditorContent(template);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-100 dark:bg-slate-900 transition-colors">
      <div className="max-w-5xl mx-auto">
        
        {/* Title Banner */}
        <div className="mb-6 border-b border-slate-200 dark:border-slate-800 pb-5">
          <h1 className="text-2xl font-extrabold tracking-tight text-black dark:text-white flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" /> User Profile & Email Signature
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Configure your personal details and rich email signature. Pasted or configured signatures will automatically append to emails you send through the CRM.
          </p>
        </div>

        {success && (
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] p-6 rounded-2xl text-sm font-black flex items-center gap-3 bg-emerald-50 dark:bg-slate-900 border-2 border-emerald-500 text-emerald-800 dark:text-emerald-300 shadow-2xl shadow-emerald-500/20 max-w-sm">
            <CheckCircle className="w-5 h-5 shrink-0 text-emerald-500" />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] p-6 rounded-2xl text-sm font-black flex items-center gap-3 bg-red-50 dark:bg-slate-900 border-2 border-red-500 text-red-800 dark:text-red-300 shadow-2xl shadow-red-500/20 max-w-sm">
            <AlertCircle className="w-5 h-5 shrink-0 text-red-500" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* Profile Info Card */}
            <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <h2 className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                <User className="w-4 h-4" /> Personal Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g. Mark Sterling"
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-slate-950 transition"
                  />
                </div>
                
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="email"
                      readOnly
                      value={userEmail}
                      className="w-full bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/80 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-400 cursor-not-allowed outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5">System Access Role</label>
                  <div className="relative">
                    <ShieldAlert className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      readOnly
                      value={role.toUpperCase()}
                      className="w-full bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/80 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-400 cursor-not-allowed outline-none font-bold uppercase tracking-wider text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5">Created At</label>
                  <div className="relative">
                    <Award className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      readOnly
                      value={createdAt ? new Date(createdAt).toLocaleDateString(undefined, { dateStyle: 'long' }) : ''}
                      className="w-full bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/80 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-400 cursor-not-allowed outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Email Signature Card */}
            <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-4 gap-3">
                <div>
                  <h2 className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-1.5">
                    <FileText className="w-4 h-4" /> CRM Email Signature
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Copy and paste your signature (including images & logos) directly into the box, or click to insert our default template.
                  </p>
                </div>
                
                <button
                  type="button"
                  onClick={insertArmelliniTemplate}
                  className="bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 hover:bg-blue-100 hover:text-blue-700 font-bold text-xs px-3.5 py-2 rounded-lg transition flex items-center gap-1.5 cursor-pointer shrink-0 border border-blue-200/50 dark:border-blue-900/50"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Use Armellini Template
                </button>
              </div>

              {/* Side-by-Side Editor & Preview Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
                {/* Left Column: Editor Control & Field */}
                <div className="space-y-3">
                  {/* Editor mode toggler */}
                  <div className="flex gap-1.5 p-1 bg-slate-100 dark:bg-slate-900 rounded-lg w-fit border border-slate-200/50 dark:border-slate-800">
                    <button
                      type="button"
                      onClick={() => {
                        setEditMode('wysiwyg');
                        setTimeout(() => {
                          if (editorRef.current) editorRef.current.innerHTML = signature;
                        }, 50);
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer ${
                        editMode === 'wysiwyg'
                          ? 'bg-white dark:bg-slate-950 text-slate-800 dark:text-white shadow-sm'
                          : 'text-slate-450 hover:text-slate-700'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Visual Editor (Paste Target)
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditMode('html')}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer ${
                        editMode === 'html'
                          ? 'bg-white dark:bg-slate-950 text-slate-800 dark:text-white shadow-sm'
                          : 'text-slate-450 hover:text-slate-700'
                      }`}
                    >
                      <Code className="w-3.5 h-3.5" /> HTML Source View
                    </button>
                  </div>

                  {/* Signature Canvas */}
                  {editMode === 'wysiwyg' ? (
                    <div className="space-y-2">
                      <div
                        ref={editorRef}
                        contentEditable={true}
                        onInput={handleEditorInput}
                        onBlur={handleEditorInput}
                        className="min-h-[290px] p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-auto outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition shadow-inner font-sans text-sm text-slate-800 dark:text-slate-200"
                        placeholder="Copy and paste your complete Gmail email signature here..."
                        style={{ whiteSpace: 'normal' }}
                      />
                      <div className="text-xs font-bold text-slate-900 dark:text-slate-100 font-mono mt-1">
                        💡 Hint: Open Gmail, select your signature, Copy it (Ctrl+C), click inside this box, and Paste it (Ctrl+V).
                      </div>
                    </div>
                  ) : (
                    <textarea
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      rows={12}
                      placeholder="Paste your raw signature HTML code here..."
                      className="w-full p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-900 text-slate-200 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition shadow-inner font-mono text-xs"
                    />
                  )}
                </div>

                {/* Right Column: Live Render Preview */}
                <div className="flex flex-col">
                  <span className="block text-[11px] font-bold text-slate-400 dark:text-slate-550 uppercase tracking-wider mb-2">Live Signature Preview</span>
                  <div className="flex-1 min-h-[300px] p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 overflow-auto flex items-start justify-start">
                    {signature ? (
                      <div className="w-full" dangerouslySetInnerHTML={{ __html: signature }} />
                    ) : (
                      <span className="text-xs text-slate-400 font-medium italic m-auto">Signature preview will appear here in real-time…</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Action */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-6 py-3 rounded-xl transition shadow-lg shadow-blue-500/20 cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {saving ? 'Saving changes…' : 'Save Profile & Signature'}
              </button>
            </div>
            
          </div>
        )}

      </div>
    </div>
  );
}
